import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  CreditCard, 
  CheckCircle, 
  AlertCircle, 
  Calendar, 
  Euro,
  Download,
  History,
  Gift,
  Shield,
  Clock,
  Users,
  Plus,
  Trash2,
  UserPlus
} from 'lucide-react';

const CotisationManagement = () => {
  const [user, setUser] = useState(null);
  const [cotisations, setCotisations] = useState([]);
  const [currentYearCotisation, setCurrentYearCotisation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cotisationAmount, setCotisationAmount] = useState(100); // Valeur par défaut
  
  // État pour le paiement multiple
  const [showMultiPayment, setShowMultiPayment] = useState(false);
  const [beneficiaries, setBeneficiaries] = useState([
    { first_name: '', last_name: '', email: '' }
  ]);
  const [multiPaymentLoading, setMultiPaymentLoading] = useState(false);

  const fetchCotisationAmount = async () => {
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cotisations/amount`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCotisationAmount(data.amount || 50);
      }
    } catch (error) {
      console.error('Erreur récupération montant cotisation:', error);
      // Garder la valeur par défaut de 50
    }
  };

  useEffect(() => {
    fetchCotisationAmount();
    fetchUserData();
    fetchCotisations();
    
    // Vérifier si on revient d'un paiement Stripe
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const success = urlParams.get('success');
    const isMulti = urlParams.get('multi');
    const isDonation = urlParams.get('donation');
    
    if (sessionId && success === 'true') {
      if (isDonation === 'true') {
        verifyDonation(sessionId);
      } else if (isMulti === 'true') {
        verifyMultiPayment(sessionId);
      } else {
        verifyPayment(sessionId);
      }
    } else if (urlParams.get('canceled') === 'true') {
      alert('Paiement annulé');
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchUserData = async () => {
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
    }
  };

  const fetchCotisations = async () => {
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/cotisations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCotisations(data);
        const currentYear = new Date().getFullYear();
        setCurrentYearCotisation(data.find(c => c.year === currentYear));
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des cotisations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayCotisation = async () => {
    try {
      const currentYear = new Date().getFullYear();
      const token = localStorage.getItem('authToken') || localStorage.getItem('memberToken');
      
      // D'abord vérifier le statut de la cotisation
      const statusResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/cotisations/status/${currentYear}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (statusResponse.ok) {
        const statusData = await statusResponse.json();
        
        // Si déjà payé, afficher un message positif
        if (statusData.status === 'paid' || statusData.status === 'payee') {
          alert('✅ ' + statusData.message);
          fetchCotisations(); // Rafraîchir l'affichage
          return;
        }
        
        // Si en attente (pending ou en_attente), demander si on veut reprendre
        if (statusData.status === 'pending' || statusData.status === 'en_attente') {
          const confirm = window.confirm(
            `Votre dernier paiement n'a pas été finalisé. Souhaitez-vous reprendre le paiement ?\n\nCliquez sur OK pour continuer vers le paiement.`
          );
          if (!confirm) return;
        }
      }
      
      // Créer la session de paiement
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/cotisations/create-checkout?year=${currentYear}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Vérifier si déjà payé (réponse du backend)
        if (data.already_paid) {
          alert('✅ ' + data.message);
          fetchCotisations();
          return;
        }
        
        // Rediriger vers Stripe Checkout
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        const error = await response.json();
        // Afficher un message plus convivial
        if (error.detail && error.detail.includes('déjà payée')) {
          alert('✅ ' + error.detail);
          fetchCotisations();
        } else {
          alert(`Une erreur est survenue. Veuillez réessayer ou contacter l'administration.\n\nDétail: ${error.detail || 'Erreur inconnue'}`);
        }
      }
    } catch (error) {
      console.error('Erreur lors du paiement:', error);
      alert('Une erreur de connexion est survenue. Veuillez vérifier votre connexion internet et réessayer.');
    }
  };

  const verifyPayment = async (sessionId, retryCount = 0) => {
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/cotisations/verify/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Vérification paiement:', data);
        
        if (data.cotisation_status === 'paid' || data.cotisation_status === 'payee' || data.payment_status === 'paid') {
          alert('✅ Paiement confirmé ! Votre cotisation a bien été enregistrée.');
          // Recharger les cotisations
          fetchCotisations();
          // Nettoyer l'URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } else if (data.payment_status === 'unpaid' && retryCount < 3) {
          // Le paiement Stripe est confirmé mais le webhook n'a peut-être pas encore mis à jour la DB
          // Attendre et réessayer
          console.log(`⏳ Paiement en attente de confirmation, tentative ${retryCount + 1}/3...`);
          setTimeout(() => {
            verifyPayment(sessionId, retryCount + 1);
          }, 2000); // Attendre 2 secondes avant de réessayer
        } else if (retryCount >= 3) {
          // Après 3 tentatives, informer l'utilisateur
          alert('⚠️ Votre paiement a été reçu par Stripe mais la confirmation prend plus de temps que prévu. Veuillez rafraîchir la page dans quelques instants. Si le problème persiste, contactez l\'administration.');
          fetchCotisations();
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else {
        // Erreur HTTP - peut-être que la session n'est pas encore enregistrée
        if (retryCount < 2) {
          console.log(`⏳ Cotisation non trouvée, tentative ${retryCount + 1}/2...`);
          setTimeout(() => {
            verifyPayment(sessionId, retryCount + 1);
          }, 2000);
        } else {
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      }
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Vérifier un paiement multiple
  const verifyMultiPayment = async (sessionId, retryCount = 0) => {
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/cotisations/verify-multi/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Vérification paiement multiple:', data);
        
        if (data.status === 'paid' || data.status === 'payee') {
          alert(`✅ Paiement confirmé ! ${data.beneficiaries_updated} cotisation(s) sur ${data.total_beneficiaries} ont été enregistrées.\n\nLes membres non trouvés dans la base seront traités manuellement par l'administration.`);
          fetchCotisations();
          window.history.replaceState({}, document.title, window.location.pathname);
        } else if (retryCount < 3) {
          // Attendre et réessayer si pas encore confirmé
          console.log(`⏳ Multi-paiement en attente, tentative ${retryCount + 1}/3...`);
          setTimeout(() => {
            verifyMultiPayment(sessionId, retryCount + 1);
          }, 2000);
        } else {
          alert('⚠️ Votre paiement a été reçu mais la confirmation prend plus de temps que prévu. Veuillez rafraîchir la page dans quelques instants.');
          fetchCotisations();
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else if (retryCount < 2) {
        setTimeout(() => {
          verifyMultiPayment(sessionId, retryCount + 1);
        }, 2000);
      } else {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.error('Erreur vérification paiement multiple:', error);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Vérifier une donation
  const verifyDonation = async (sessionId, retryCount = 0) => {
    try {
      const token = localStorage.getItem('memberToken') || localStorage.getItem('authToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/donations/verify/${sessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Vérification donation:', data);
        
        if (data.status === 'paid') {
          alert('✅ ' + data.message);
          window.history.replaceState({}, document.title, window.location.pathname);
        } else if (retryCount < 3) {
          console.log(`⏳ Donation en attente, tentative ${retryCount + 1}/3...`);
          setTimeout(() => {
            verifyDonation(sessionId, retryCount + 1);
          }, 2000);
        } else {
          alert('⚠️ Votre donation a été reçue mais la confirmation prend plus de temps que prévu. Merci pour votre générosité !');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } else if (retryCount < 2) {
        setTimeout(() => {
          verifyDonation(sessionId, retryCount + 1);
        }, 2000);
      } else {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    } catch (error) {
      console.error('Erreur vérification donation:', error);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  // Ajouter un bénéficiaire
  const addBeneficiary = () => {
    if (beneficiaries.length < 20) {
      setBeneficiaries([...beneficiaries, { first_name: '', last_name: '', email: '' }]);
    }
  };

  // Supprimer un bénéficiaire
  const removeBeneficiary = (index) => {
    if (beneficiaries.length > 1) {
      setBeneficiaries(beneficiaries.filter((_, i) => i !== index));
    }
  };

  // Mettre à jour un bénéficiaire
  const updateBeneficiary = (index, field, value) => {
    const updated = [...beneficiaries];
    updated[index][field] = value;
    setBeneficiaries(updated);
  };

  // Calculer le montant total
  const totalAmount = beneficiaries.length * cotisationAmount;

  // Payer pour plusieurs membres
  const handleMultiPayment = async () => {
    // Valider les données
    const validBeneficiaries = beneficiaries.filter(b => b.first_name.trim() && b.last_name.trim());
    
    if (validBeneficiaries.length === 0) {
      alert('Veuillez renseigner au moins un bénéficiaire (nom et prénom obligatoires)');
      return;
    }

    // Vérifier que tous les bénéficiaires ont nom et prénom
    const invalidIndex = beneficiaries.findIndex(b => 
      (b.first_name.trim() || b.last_name.trim() || b.email.trim()) && 
      (!b.first_name.trim() || !b.last_name.trim())
    );
    
    if (invalidIndex !== -1) {
      alert(`Le bénéficiaire ${invalidIndex + 1} doit avoir un nom ET un prénom`);
      return;
    }

    setMultiPaymentLoading(true);
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('memberToken');
      const currentYear = new Date().getFullYear();
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/cotisations/create-multi-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          beneficiaries: validBeneficiaries,
          year: currentYear
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.url) {
          window.location.href = data.url;
        }
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail || 'Une erreur est survenue'}`);
      }
    } catch (error) {
      console.error('Erreur paiement multiple:', error);
      alert('Une erreur de connexion est survenue');
    } finally {
      setMultiPaymentLoading(false);
    }
  };

  const handleMakeDonation = async () => {
    // Demander le montant de la donation
    const amount = prompt('Montant de la donation en euros (montant libre):');
    
    if (!amount) return;
    
    const parsedAmount = parseFloat(amount);
    if (isNaN(parsedAmount) || parsedAmount < 1) {
      alert('Veuillez entrer un montant valide (minimum 1€)');
      return;
    }
    
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('memberToken');
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/member/donations/create-checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ amount: parsedAmount })
      });

      if (response.ok) {
        const data = await response.json();
        // Rediriger vers Stripe Checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        alert(`Erreur: ${error.detail || 'Impossible de créer la session de donation'}`);
      }
    } catch (error) {
      console.error('Erreur lors de la création de la donation:', error);
      alert('Une erreur est survenue lors de la création de la donation');
    }
  };

  const getRoleInfo = (role) => {
    switch (role) {
      case 'membre_vie':
        return {
          label: 'Membre à vie',
          description: 'Pas de cotisation annuelle requise, donation possible.',
          color: 'text-green-800',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-200',
          icon: <Shield className="w-6 h-6 text-green-600" />,
          requiresCotisation: false
        };
      case 'membre_annuel':
        return {
          label: 'Membre annuel',
          description: 'Cotisation annuelle requise au 1er janvier.',
          color: 'text-white',
          bgColor: 'bg-[rgb(196,150,96)]', 
          borderColor: 'border-[rgb(196,150,96)]',
          icon: <Calendar className="w-6 h-6 text-white" />,
          requiresCotisation: true
        };
      case 'membre_exterieur':
        return {
          label: 'Membre extérieur',
          description: 'Pas de cotisation annuelle requise, donation possible.',
          color: 'text-gray-800',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-200', 
          icon: <Gift className="w-6 h-6 text-gray-600" />,
          requiresCotisation: false
        };
      case 'admin':
        return {
          label: 'Administrateur',
          description: 'Pas de cotisation annuelle requise, donation possible.',
          color: 'text-amber-800',
          bgColor: 'bg-amber-50',
          borderColor: 'border-amber-200', 
          icon: <Shield className="w-6 h-6" style={{ color: 'rgb(196, 150, 96)' }} />,
          requiresCotisation: false
        };
      default:
        return {
          label: 'Membre',
          description: 'Cotisation annuelle requise au 1er janvier.',
          color: 'text-gray-800',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-200',
          icon: <Calendar className="w-6 h-6 text-gray-600" />,
          requiresCotisation: true
        };
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'paid':
      case 'payee':
        return {
          label: 'Payée',
          color: 'text-green-800',
          bgColor: 'bg-green-100',
          icon: <CheckCircle className="w-4 h-4 text-green-600" />
        };
      case 'pending':
      case 'en_attente':
        return {
          label: 'En attente',
          color: 'text-orange-800',
          bgColor: 'bg-orange-100', 
          icon: <Clock className="w-4 h-4 text-orange-600" />
        };
      case 'cancelled':
        return {
          label: 'Annulée',
          color: 'text-red-800',
          bgColor: 'bg-red-100',
          icon: <AlertCircle className="w-4 h-4 text-red-600" />
        };
      default:
        return {
          label: status,
          color: 'text-gray-800',
          bgColor: 'bg-gray-100',
          icon: <AlertCircle className="w-4 h-4 text-gray-600" />
        };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Chargement des informations de cotisation...</div>
      </div>
    );
  }

  const roleInfo = getRoleInfo(user?.role);
  const currentYear = new Date().getFullYear();

  return (
    <div className="space-y-6">
      <h1 
        className="text-3xl font-bold" 
        style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
      >
        Ma Cotisation
      </h1>

      {/* Alerte pour membres annuels - Devenir membre à vie */}
      {user?.role === 'membre_annuel' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Shield className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800" style={{ fontFamily: '"Lora", serif' }}>
                Cotisation annuelle
              </h3>
              <p className="text-amber-700 mt-1">
                Vous êtes membre avec une cotisation annuelle. Si vous souhaitez devenir membre à vie, 
                il vous suffit d'en faire la demande par email à{' '}
                <a 
                  href="mailto:lignagesdebruxelles@gmail.com" 
                  className="font-semibold underline hover:text-amber-900"
                >
                  lignagesdebruxelles@gmail.com
                </a>. 
                Dès réception de votre message, nous vous recontacterons dans les plus brefs délais.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Statut du membre et cotisation actuelle */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Type de membre */}
        <Card className={`border ${roleInfo.borderColor}`}>
          <CardHeader className={roleInfo.bgColor}>
            <CardTitle 
              className={`flex items-center ${roleInfo.color}`}
              style={{ fontFamily: '"Lora", serif' }}
            >
              {roleInfo.icon}
              <span className="ml-2">{roleInfo.label}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {/* Afficher nom et email pour membre annuel */}
            {user?.role === 'membre_annuel' && (
              <div className="mb-4">
                <p className="font-semibold text-gray-800">{user?.first_name} {user?.last_name}</p>
                <p className="text-sm text-gray-600">{user?.email}</p>
                <hr className="my-3 border-gray-300" />
                <p className="text-gray-600">
                  {roleInfo.description}
                </p>
              </div>
            )}
            
            {user?.role !== 'membre_annuel' && (
              <p className="text-gray-600 mb-4">
                {roleInfo.description}
              </p>
            )}
            
            {user?.role === 'membre_vie' && (
              <div className="text-center py-4">
                <Shield className="w-16 h-16 text-green-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-green-700">
                  Adhésion à vie active
                </p>
                <p className="text-sm text-gray-600">
                  Depuis {user?.membership_start_date ? 
                    new Date(user?.membership_start_date).getFullYear() : 
                    'toujours'
                  }
                </p>
              </div>
            )}
            
            {user?.role === 'membre_exterieur' && (
              <div className="text-center py-4">
                <Button 
                  onClick={handleMakeDonation}
                  className="text-white"
                  style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Faire une donation
                </Button>
              </div>
            )}
            
            {(user?.role === 'membre_vie' || user?.role === 'admin') && (
              <div className="text-center py-4">
                <Button 
                  onClick={handleMakeDonation}
                  className="text-white"
                  style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                >
                  <Gift className="w-4 h-4 mr-2" />
                  Faire une donation
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cotisation année courante */}
        {user?.role === 'membre_annuel' && (
          <Card>
            <CardHeader style={{ backgroundColor: 'rgb(196, 150, 96)' }}>
              <CardTitle 
                className="flex items-center text-white"
                style={{ fontFamily: '"Lora", serif' }}
              >
                <Calendar className="w-5 h-5 mr-2" />
                Cotisation {currentYear}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {currentYearCotisation && (currentYearCotisation.status === 'paid' || currentYearCotisation.status === 'payee') ? (
                /* Cotisation payée */
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold">
                      €{currentYearCotisation.amount}
                    </span>
                    <div className="flex items-center space-x-2">
                      {getStatusInfo(currentYearCotisation.status).icon}
                      <span 
                        className={`px-2 py-1 rounded-full text-sm font-medium ${getStatusInfo(currentYearCotisation.status).bgColor} ${getStatusInfo(currentYearCotisation.status).color}`}
                      >
                        {getStatusInfo(currentYearCotisation.status).label}
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-medium flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Cotisation {currentYear} payée
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      Payé le {currentYearCotisation.paid_at ? new Date(currentYearCotisation.paid_at).toLocaleDateString('fr-FR') : '-'}
                    </p>
                  </div>
                </div>
              ) : currentYearCotisation && (currentYearCotisation.status === 'pending' || currentYearCotisation.status === 'en_attente') ? (
                /* Paiement en attente - proposer de reprendre */
                <div className="space-y-4">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <p className="text-orange-800 font-medium flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Paiement non finalisé
                    </p>
                    <p className="text-sm text-orange-600 mt-1">
                      Votre dernier paiement n'a pas été finalisé. Souhaitez-vous reprendre le paiement ?
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handlePayCotisation}
                    className="w-full text-white"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Reprendre le paiement €{cotisationAmount.toFixed(2)}
                  </Button>
                </div>
              ) : (
                /* Pas de cotisation - afficher le bouton de paiement */
                <div className="space-y-4">
                  <div className="border rounded-lg p-4" style={{ backgroundColor: 'rgba(196, 150, 96, 0.1)', borderColor: 'rgb(196, 150, 96)' }}>
                    <p className="font-medium flex items-center" style={{ color: 'rgb(196, 150, 96)' }}>
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Cotisation {currentYear} due
                    </p>
                    <p className="text-sm mt-1" style={{ color: 'rgb(156, 120, 76)' }}>
                      Montant: €{cotisationAmount.toFixed(2)} - Échéance: 1er janvier {currentYear}
                    </p>
                  </div>
                  
                  <Button 
                    onClick={handlePayCotisation}
                    className="w-full text-white"
                    style={{ backgroundColor: 'rgb(196, 150, 96)' }}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Payer ma cotisation €{cotisationAmount.toFixed(2)}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Section Payer pour plusieurs membres */}
      <Card>
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setShowMultiPayment(!showMultiPayment)}
        >
          <CardTitle 
            className="flex items-center justify-between"
            style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
          >
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Payer pour plusieurs membres
            </div>
            <span className="text-sm font-normal text-gray-500">
              {showMultiPayment ? '▼ Fermer' : '▶ Ouvrir'}
            </span>
          </CardTitle>
        </CardHeader>
        
        {showMultiPayment && (
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                <strong>💡 Astuce :</strong> Vous pouvez payer les cotisations de plusieurs membres en une seule transaction. 
                Renseignez le nom, prénom et si possible l'email de chaque bénéficiaire.
              </p>
            </div>

            {/* Liste des bénéficiaires */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-700">Bénéficiaires ({beneficiaries.length})</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addBeneficiary}
                  disabled={beneficiaries.length >= 20}
                  className="flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter
                </Button>
              </div>

              {beneficiaries.map((beneficiary, index) => (
                <div key={index} className="border rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-sm text-gray-600">
                      Bénéficiaire {index + 1}
                    </span>
                    {beneficiaries.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBeneficiary(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs text-gray-500">Prénom *</Label>
                      <Input
                        type="text"
                        placeholder="Prénom"
                        value={beneficiary.first_name}
                        onChange={(e) => updateBeneficiary(index, 'first_name', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Nom *</Label>
                      <Input
                        type="text"
                        placeholder="Nom"
                        value={beneficiary.last_name}
                        onChange={(e) => updateBeneficiary(index, 'last_name', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Email (recommandé)</Label>
                      <Input
                        type="email"
                        placeholder="email@exemple.com"
                        value={beneficiary.email}
                        onChange={(e) => updateBeneficiary(index, 'email', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Récapitulatif et bouton de paiement */}
            <div className="border-t pt-4 space-y-4">
              <div className="flex items-center justify-between text-lg">
                <span className="font-semibold">Montant total :</span>
                <span className="font-bold text-2xl" style={{ color: 'rgb(196, 150, 96)' }}>
                  €{totalAmount.toFixed(2)}
                </span>
              </div>
              <p className="text-sm text-gray-500">
                {beneficiaries.length} cotisation(s) × €{cotisationAmount.toFixed(2)}
              </p>
              
              <Button
                onClick={handleMultiPayment}
                disabled={multiPaymentLoading || beneficiaries.every(b => !b.first_name.trim() && !b.last_name.trim())}
                className="w-full text-white py-3"
                style={{ backgroundColor: 'rgb(196, 150, 96)' }}
              >
                {multiPaymentLoading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Préparation du paiement...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5 mr-2" />
                    Payer €{totalAmount.toFixed(2)} pour {beneficiaries.filter(b => b.first_name.trim() && b.last_name.trim()).length} membre(s)
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Historique des cotisations et donations */}
      <Card>
        <CardHeader>
          <CardTitle 
            className="flex items-center"
            style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
          >
            <History className="w-5 h-5 mr-2" />
            Historique des cotisations et donations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {cotisations.length > 0 ? (
            <div className="space-y-4">
              {cotisations.map((cotisation) => (
                <div key={cotisation.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold" style={{ color: 'rgb(196, 150, 96)' }}>
                          {cotisation.year}
                        </div>
                        <div className="text-sm text-gray-600">Année</div>
                      </div>
                      <div>
                        <div className="font-semibold">
                          €{cotisation.amount} {cotisation.currency}
                        </div>
                        <div className="flex items-center space-x-2 mt-1">
                          {getStatusInfo(cotisation.status).icon}
                          <span 
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusInfo(cotisation.status).bgColor} ${getStatusInfo(cotisation.status).color}`}
                          >
                            {getStatusInfo(cotisation.status).label}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      {cotisation.paid_at && (
                        <div className="text-sm text-gray-600">
                          Payé le {new Date(cotisation.paid_at).toLocaleDateString('fr-FR')}
                        </div>
                      )}
                      {(cotisation.status === 'paid' || cotisation.status === 'payee') && (
                        <Button variant="outline" size="sm" className="mt-2">
                          <Download className="w-4 h-4 mr-1" />
                          Reçu
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Aucun historique de cotisation</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Informations importantes */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">
                Informations sur les cotisations
              </h3>
              <div className="text-sm text-blue-800 space-y-1">
                <p>• <strong>Membres à vie :</strong> Pas de cotisation annuelle requise</p>
                <p>• <strong>Membres annuels :</strong> Cotisation de €{cotisationAmount} due au 1er janvier de chaque année</p>
                <p>• <strong>Membres extérieurs :</strong> Pas de cotisation, donations volontaires bienvenues</p>
                <p>• <strong>Paiement :</strong> Sécurisé via Stripe, cartes de crédit acceptées</p>
                <p>• <strong>Reçus :</strong> Disponibles en téléchargement après paiement</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CotisationManagement;